import { Link } from "react-router-dom";

/* ─────────────────────────────────────────
   TRACKS SECTION — قسم المسارات الرئيسية
   Landing page deep-dive between Programs & Store
   ───────────────────────────────────────── */

const TRACKS = [
  {
    to: "/ai-track",
    accent: "#D85A30",
    accentDim: "rgba(216,90,48,.08)",
    accentBorder: "rgba(216,90,48,.2)",
    accentHover: "rgba(216,90,48,.28)",
    icon: "🤖",
    tag: "AI IN BUSINESS",
    title: "مسار الذكاء الاصطناعي",
    subtitle: "في الأعمال",
    desc: "من تحليل البيانات بـ Python وبناء نماذج ML إلى نشر تطبيق AI حقيقي — ٥ بوابات تبني فيها خبرة سوقية قابلة للإثبات.",
    modules: [
      "تحليل البيانات بواسطة AI للوصول إلى قرارات ذكية",
      "تعلم الآلة وبناء النماذج والتعامل معها",
      "معالجة اللغة الطبيعية وبناء نماذج NLP",
      "ربط الذكاء الاصطناعي بالأعمال",
      "بناء وتطوير تطبيقات الذكاء الاصطناعي",
    ],
    tools: [
      "Python",
      "Pandas",
      "HuggingFace",
      "Power BI",
      "Streamlit",
      "GitHub Actions",
    ],
    stats: [
      { n: "٥", l: "بوابات" },
      { n: "٣٠٠+", l: "ساعة" },
      { n: "١٣٥٠", l: "ر / بوابة" },
    ],
    bundle: { price: "٤٦٢٥", original: "٦٧٥٠" },
    ctaLabel: "استعرض مسار AI",
    projects: ["قرينفاي", "رفيق", "مداد", "AIM"],
  },
  {
    to: "/cyber-track",
    accent: "#1A9070",
    accentDim: "rgba(26,144,112,.08)",
    accentBorder: "rgba(26,144,112,.2)",
    accentHover: "rgba(26,144,112,.28)",
    icon: "🔐",
    tag: "CYBERSECURITY",
    // ── اسم المسار الرسمي المحدَّث من بنر سلة ──
    title: "مسار الأمن السيبراني",
    subtitle: "في البرمجيات",
    desc: "من أساسيات الهجوم والدفاع إلى Pentest كامل مع تقرير أمني احترافي — ٥ بوابات تؤهلك لسوق أمن المعلومات.",
    // ── ترتيب البوابات G1→G5 حسب المتجر ──
    modules: [
      "أساسيات الأمن السيبراني والهندسة الاجتماعية",
      "أساسيات أمن تطبيقات الويب — OWASP",
      "أساسيات أمن الشبكات وتحليل الحزم",
      "DevSecOps وأمن الأكواد والتطبيقات",
      "اختبار الاختراق والتقارير الأمنية",
    ],
    tools: [
      "Kali Linux",
      "Burp Suite",
      "Wireshark",
      "Nmap",
      "GitHub Actions",
      "OWASP",
    ],
    stats: [
      { n: "٥", l: "بوابات" },
      { n: "٣٠٠+", l: "ساعة" },
      { n: "١٣٥٠", l: "ر / بوابة" },
    ],
    bundle: { price: "٤٦٢٥", original: "٦٧٥٠" },
    ctaLabel: "استعرض مسار Cyber",
    projects: ["وثيق", "CyberHook", "NWSA"],
  },
];

export default function TracksSection() {
  return (
    <section
      className="relative overflow-hidden px-[clamp(16px,5vw,80px)] py-[clamp(64px,9vh,110px)]"
      id="tracks"
    >
      {/* top divider */}
      <div
        className="absolute top-0 left-[clamp(16px,5vw,80px)] right-[clamp(16px,5vw,80px)] h-px"
        style={{
          background:
            "linear-gradient(90deg,transparent,rgba(109,211,167,0.11) 30%,rgba(109,211,167,0.11) 70%,transparent)",
        }}
      />

      {/* bg atmosphere */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 40% at 50% 0%,rgba(109,211,167,0.03) 0%,transparent 65%)",
        }}
      />

      {/* ── Header ── */}
      <div className="relative z-[2] text-center mb-[clamp(36px,5vh,60px)]">
        <span className="inline-flex items-center gap-2 font-mono text-[.68rem] tracking-[.2em] uppercase text-[#6DD3A7] font-semibold mb-[1rem] before:content-['◆'] before:text-[.4rem] before:opacity-40 after:content-['◆'] after:text-[.4rem] after:opacity-40">
          FLAGSHIP TRACKS · المسارات الرئيسية
        </span>
        <h2
          className="font-cairo font-black leading-[1.1] tracking-[-0.03em] text-[#F7F2E9] mb-[.6rem]"
          style={{ fontSize: "clamp(1.9rem,3.5vw,3.1rem)" }}
        >
          اختر <em className="not-italic text-[#6DD3A7]">مسارك</em>
        </h2>
        <p className="text-[.93rem] text-[#94B4C8] leading-[1.85] max-w-[500px] mx-auto">
          مساران رئيسيان — كل واحد ١٠ أسابيع مكثفة تخرج منها بمشروع حقيقي وشهادة
          معتمدة.
        </p>
      </div>

      {/* ── Track Cards ── */}
      <div className="relative z-[2] grid gap-[clamp(14px,2vw,24px)] lg:grid-cols-2">
        {TRACKS.map((t) => (
          <div
            key={t.to}
            className="rounded-[16px] border bg-[#0C1F35] overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-[4px]"
            style={{
              borderColor: t.accentBorder,
              boxShadow: "0 4px 32px rgba(8,24,40,.4)",
            }}
          >
            {/* accent bar */}
            <div
              className="h-[3px] flex-shrink-0"
              style={{
                background: `linear-gradient(90deg,${t.accent},${t.accent}88)`,
              }}
            />

            <div className="p-[clamp(22px,2.8vw,36px)] flex flex-col flex-1">
              {/* top row */}
              <div className="flex items-start justify-between gap-4 mb-[20px]">
                <div>
                  <span
                    className="inline-flex items-center gap-1.5 font-mono text-[.52rem] tracking-[.14em] uppercase font-bold px-[9px] py-[3px] rounded-full border mb-[10px]"
                    style={{
                      background: t.accentDim,
                      borderColor: t.accentBorder,
                      color: t.accent,
                    }}
                  >
                    <span className="w-[5px] h-[5px] rounded-full bg-current animate-pulse" />
                    {t.tag}
                  </span>
                  <h3
                    className="font-cairo font-black leading-[1.1] tracking-tight"
                    style={{
                      fontSize: "clamp(1.3rem,2vw,1.8rem)",
                      color: t.accent,
                    }}
                  >
                    {t.title}
                  </h3>
                  <p className="font-cairo font-bold text-[#F7F2E9] text-[clamp(.95rem,1.2vw,1.1rem)]">
                    {t.subtitle}
                  </p>
                </div>
                <span className="text-[3rem] flex-shrink-0 leading-none">
                  {t.icon}
                </span>
              </div>

              <p className="text-[.86rem] text-[#94B4C8] leading-[1.8] mb-[22px]">
                {t.desc}
              </p>

              {/* modules list — 5 gates */}
              <div className="mb-[22px]">
                <p
                  className="font-mono text-[.5rem] tracking-[.14em] uppercase mb-[10px]"
                  style={{ color: t.accent }}
                >
                  البوابات الخمس
                </p>
                <div className="space-y-[6px]">
                  {t.modules.map((m, i) => (
                    <div key={m} className="flex items-center gap-3">
                      <span
                        className="flex-shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center font-mono text-[.46rem] font-bold"
                        style={{
                          background: t.accentDim,
                          border: `1px solid ${t.accentBorder}`,
                          color: t.accent,
                        }}
                      >
                        G{i + 1}
                      </span>
                      <span className="text-[.78rem] text-[#94B4C8]">{m}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* tools */}
              <div className="flex flex-wrap gap-1.5 mb-[22px]">
                {t.tools.map((tool) => (
                  <span
                    key={tool}
                    className="text-[.62rem] px-[8px] py-[2px] rounded-full border"
                    style={{
                      background: t.accentDim,
                      borderColor: t.accentBorder,
                      color: t.accent,
                    }}
                  >
                    {tool}
                  </span>
                ))}
              </div>

              {/* divider */}
              <div
                className="h-px mb-[20px]"
                style={{ background: t.accentBorder }}
              />

              {/* stats + bundle price */}
              <div className="grid grid-cols-4 gap-[10px] mb-[24px]">
                {t.stats.map((s) => (
                  <div
                    key={s.l}
                    className="text-center rounded-[8px] p-[10px]"
                    style={{ background: t.accentDim }}
                  >
                    <span
                      className="block font-cairo font-black text-[1.15rem] leading-none mb-[2px]"
                      style={{ color: t.accent }}
                    >
                      {s.n}
                    </span>
                    <span className="block font-mono text-[.48rem] text-[#3D6278] tracking-[.08em] uppercase">
                      {s.l}
                    </span>
                  </div>
                ))}
                {/* bundle cell */}
                <div
                  className="text-center rounded-[8px] p-[10px] col-span-1"
                  style={{
                    background: "rgba(242,185,73,.07)",
                    border: "1px solid rgba(242,185,73,.18)",
                  }}
                >
                  <span className="block font-cairo text-[.5rem] text-[#3D6278] line-through leading-none">
                    {t.bundle.original}
                  </span>
                  <span className="block font-cairo font-black text-[.95rem] leading-none mb-[2px] text-[#F2B949]">
                    {t.bundle.price}
                  </span>
                  <span className="block font-mono text-[.44rem] text-[#3D6278] tracking-[.06em] uppercase">
                    باقة كاملة
                  </span>
                </div>
              </div>

              {/* projects */}
              <div className="flex items-center gap-[6px] mb-[22px] flex-wrap">
                <span className="font-mono text-[.48rem] text-[#3D6278] tracking-[.1em] uppercase ml-[4px]">
                  مشاريع خريجين:
                </span>
                {t.projects.map((p) => (
                  <span
                    key={p}
                    className="font-cairo font-bold text-[.7rem] px-[8px] py-[2px] rounded-full"
                    style={{
                      background: "rgba(247,242,233,.05)",
                      border: "1px solid rgba(247,242,233,.09)",
                      color: "#94B4C8",
                    }}
                  >
                    {p}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-auto flex items-center gap-[10px]">
                <Link
                  to={t.to}
                  className="flex-1 flex items-center justify-center gap-2 font-cairo font-bold py-[12px] px-[20px] rounded-[4px] text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-[1px] text-[.9rem]"
                  style={{ background: t.accent }}
                >
                  <svg
                    className="w-[14px] h-[14px] flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  {t.ctaLabel}
                </Link>
                <a
                  href="https://store.afaaqwetaan.com"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center justify-center gap-1.5 font-cairo font-medium py-[12px] px-[16px] rounded-[4px] border text-[.82rem] transition-all duration-200"
                  style={{ borderColor: t.accentBorder, color: t.accent }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.accentDim;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  سجّل الآن
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tamara note ── */}
      <p className="relative z-[2] text-center mt-[clamp(16px,2vh,24px)] font-mono text-[.58rem] text-[#3D6278] tracking-[.06em]">
        ◆ دفع بالتقسيط عبر تمارا متاح لجميع المسارات — يظهر عند الدفع في سلة
      </p>
    </section>
  );
}
